import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CenteredSpinner } from '../../components/ui/Spinner';
import type { PurgeReason } from '../../lib/portalAssets/purge';
import { assetDisplayName } from '../../lib/portalAssets/gridView';
import { categoriesState } from './AssetCategoryPicker';
import { AssetDetailView } from './AssetDetailView';
import { AssetHistoryList } from './AssetHistoryList';
import { ClearAssetControl } from './ClearAssetControl';
import { DeleteAssetControl } from './DeleteAssetControl';
import { PurgeAssetControl } from './PurgeAssetControl';
import { ReviewControls } from './ReviewControls';
import { purgePortalAsset, restorePortalAsset, staffAssetsApi } from './staffApi';
import { useImageErrorRefetch } from './useImageErrorRefetch';

// THE STAFF ASSET DIALOG — one asset, with review, delete / restore / purge, tags,
// categories and history. Shared by the account page's Assets tab and the
// cross-account list, so there is ONE implementation of what staff can do to an
// asset (a second copy would drift).
//
// ⚠️ STAFF ENDPOINTS ONLY (staffApi.ts); no account scope is needed — every call is
// by asset id and the server applies the staff guard per action. The category list
// comes from /categories/for-account keyed by the asset's own account, so a surface
// that does not know the asset's brand can still populate the picker.
//
// ⚠️ EVERY mutation invalidates the whole ['portal-assets-staff'] key, which prefixes
// both hosts' list queries — so the grid behind the dialog REFETCHES and agrees with
// what just happened (an approved asset leaves a "Review needed" list).
export function StaffAssetDialog({ assetId, onClose }: { assetId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const open = assetId !== null;
  const detail = useQuery({
    queryKey: ['portal-assets-staff', 'detail', assetId],
    queryFn: () => staffAssetsApi.detail(assetId!),
    enabled: open,
    retry: false,
  });
  const history = useQuery({
    queryKey: ['portal-assets-staff', 'history', assetId],
    queryFn: () => staffAssetsApi.history(assetId!),
    enabled: open,
    retry: false,
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['portal-assets-staff'] });
  const review = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; reason?: string }) => staffAssetsApi.review(assetId!, input),
    onSuccess: invalidate,
  });
  const detailAccountId = detail.data?.accountId;
  // ⚠️ The brand is derived server-side from this account; no brand id is sent.
  const categories = useQuery({
    queryKey: ['portal-assets-staff', 'categories-for-account', detailAccountId],
    queryFn: () => staffAssetsApi.categories(detailAccountId!),
    enabled: detailAccountId !== undefined,
    retry: false,
  });
  const saveCategories = useMutation({
    mutationFn: (categoryIds: string[]) => staffAssetsApi.patch(assetId!, { categoryIds }),
    onSuccess: invalidate,
  });
  const saveTitle = useMutation({
    mutationFn: (title: string | null) => staffAssetsApi.patch(assetId!, { title }),
    onSuccess: invalidate,
  });
  const saveTags = useMutation({
    mutationFn: (tags: string[]) => staffAssetsApi.patch(assetId!, { tags }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: () => staffAssetsApi.remove(assetId!), onSuccess: invalidate });
  const restore = useMutation({ mutationFn: () => restorePortalAsset(assetId!), onSuccess: invalidate });
  // ⚠️ A purged asset answers 404 to everyone, so on success the dialog closes
  // rather than refetching a detail that no longer exists.
  const purge = useMutation({
    mutationFn: (input: { reason: PurgeReason; note: string | null }) => purgePortalAsset(assetId!, input),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const onDetailImageError = useImageErrorRefetch(detail.refetch);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          review.reset();
          saveTags.reset();
          saveTitle.reset();
          remove.reset();
          restore.reset();
          purge.reset();
        }
      }}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="break-all">{detail.data ? assetDisplayName(detail.data) : 'Asset'}</DialogTitle>
        </DialogHeader>
        {detail.isPending ? (
          <CenteredSpinner />
        ) : detail.isError ? (
          <p className="text-sm text-muted-foreground">Couldn't load this asset.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start gap-2">
              <DeleteAssetControl
                key={`delete-${detail.data.id}`}
                tile={detail.data}
                audience="staff"
                onDelete={() => remove.mutate(undefined, { onSuccess: () => void detail.refetch() })}
                pending={remove.isPending}
                error={remove.error ? remove.error.message : null}
              />
              {detail.data.deletedDaysLeft !== null && (
                <Button
                  size="sm"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(undefined, { onSuccess: () => void detail.refetch() })}
                >
                  {restore.isPending ? 'Restoring…' : 'Restore'}
                </Button>
              )}
              <ClearAssetControl
                key={`clear-${detail.data.id}`}
                assetId={detail.data.id}
                hasAnything={detail.data.title !== null || detail.data.tags.length > 0 || detail.data.categoryIds.length > 0}
                onDone={() => {
                  invalidate();
                  void detail.refetch();
                }}
              />
              <PurgeAssetControl
                key={`purge-${detail.data.id}`}
                asset={detail.data}
                onPurge={(input) => purge.mutate(input)}
                pending={purge.isPending}
                error={purge.error ? purge.error.message : null}
              />
              {restore.error && (
                <p className="text-sm text-destructive" role="alert">
                  {restore.error.message}
                </p>
              )}
            </div>
            <ReviewControls
              key={detail.data.review.status + (detail.data.review.reason ?? '')}
              audience="staff"
              tile={detail.data}
              onReview={(input) => review.mutate(input, { onSuccess: () => void detail.refetch() })}
              pending={review.isPending}
              error={review.error ? review.error.message : null}
            />
            <AssetDetailView
              key={detail.data.id + (detail.data.title ?? '') + detail.data.tags.join(',') + detail.data.categoryIds.join(',')}
              detail={detail.data}
              audience="staff"
              onSaveTitle={(title) => saveTitle.mutate(title, { onSuccess: () => void detail.refetch() })}
              titleSaving={saveTitle.isPending}
              titleError={saveTitle.error ? saveTitle.error.message : null}
              onSaveTags={(tags) => saveTags.mutate(tags, { onSuccess: () => void detail.refetch() })}
              saving={saveTags.isPending}
              saveError={saveTags.error ? saveTags.error.message : null}
              categories={categoriesState(categories)}
              onSaveCategories={(ids) => saveCategories.mutate(ids, { onSuccess: () => void detail.refetch() })}
              categoriesSaving={saveCategories.isPending}
              categoriesError={saveCategories.error ? saveCategories.error.message : null}
              onImageError={onDetailImageError}
              // AI suggestion (Run D). On-demand: only the button calls this; the suggestion writes nothing until Accept.
              onEnrich={() => staffAssetsApi.enrich(detail.data.id)}
              onAcceptEnrichment={(patch) =>
                staffAssetsApi.patch(detail.data.id, patch).then(async () => {
                  invalidate();
                  await detail.refetch();
                })
              }
            />
            <AssetHistoryList entries={history.data?.entries} loading={history.isPending} failed={history.isError} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
